import React from 'react';
import { calculateWeaponStats, getUnarmedStats } from '../utils/combatUtils';
import './Combat.css';

const CONDITIONS = [
    'Concentrating', 'Invisible', // Positive/Neutral first
    'Blinded', 'Charmed', 'Deafened', 'Frightened',
    'Grappled', 'Incapacitated', 'Paralyzed', 'Petrified',
    'Poisoned', 'Prone', 'Restrained', 'Stunned', 'Unconscious', 'Exhaustion'
];

const Combat = ({ character, proficiencyBonus, updateResource, rest, toggleCondition }) => {
    const { inventory, abilityScores, resources, maxResources } = character;
    const equippedWeapons = inventory.weapons.filter(w => w.equipped);
    const unarmed = getUnarmedStats(character);

    const validResources = Object.entries(maxResources).filter(([key, max]) => max && max > 0 && !key.startsWith('spellSlot'));

    const handleRest = (type) => {
        if (window.confirm(`Take a ${type === 'short' ? 'Short' : 'Long'} Rest?`)) {
            rest(type);
        }
    };

    // Class Actions


    // Prepared Spells


    const STANDARD_ACTIONS = [
        { name: 'Attack', desc: 'Perform a melee or ranged attack' },
        { name: 'Cast a Spell', desc: 'Cast a spell with a casting time of 1 Action' },
        { name: 'Dash', desc: 'Double your movement speed for the turn' },
        { name: 'Disengage', desc: 'Prevent opportunity attacks for the rest of the turn' },
        { name: 'Dodge', desc: 'Attacks against you have disadvantage' },
        { name: 'Help', desc: 'Give advantage to an ally on their next check/attack' },
        { name: 'Hide', desc: 'Make a Stealth check to become unseen' },
        { name: 'Ready', desc: 'Trigger an action in response to a specific event' },
        { name: 'Search', desc: 'Devote your attention to finding something' },
        { name: 'Use an Object', desc: 'Interact with an object (e.g., drink potion)' },
    ];

    return (
        <div className="combat-container">
            {/* Top Toolbar: Rest (Left) and Conditions (Right) */}
            <div className="combat-toolbar">
                <div className="rest-buttons">
                    <button className="rest-btn short" onClick={() => handleRest('short')}>Short Rest</button>
                    <button className="rest-btn long" onClick={() => handleRest('long')}>Long Rest</button>
                </div>

                <div className="conditions-toolbar">
                    <span className="conditions-label">Conditions:</span>
                    <div className="conditions-list">
                        {CONDITIONS.map(cond => {
                            const isActive = character.activeConditions?.includes(cond);
                            const isPositive = ['Concentrating', 'Invisible'].includes(cond);
                            return (
                                <button
                                    key={cond}
                                    className={`condition-btn ${isActive ? 'active' : ''} ${isPositive ? 'positive' : ''}`}
                                    onClick={() => toggleCondition(cond)}
                                    title={`Toggle ${cond}`}
                                >
                                    {cond}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Resources Section (Dynamic - Non-Spell Slots) */}
            {validResources.length > 0 && (
                <div className="combat-section resources-section">
                    {validResources.map(([key, max]) => {
                        const val = resources[key];
                        // Default to MAX if undefined (assuming fresh start), consistent with spell slots
                        const current = val !== undefined ? val : max;

                        // Resource Colors
                        const colors = {
                            ki: '#64d2ff',       // Cyan/Blue
                            rage: '#ff5500',     // Orange/Red
                            sorceryPoints: '#d844ff', // Purple
                            superiorityDice: '#ffcc00', // Gold
                        };
                        const color = colors[key] || '#4caf50';
                        let label = key.replace(/([A-Z])/g, ' $1').trim();

                        return (
                            <div key={key} className="resource-tracker" style={{ '--res-color': color }}>
                                <h4>{label}</h4>
                                <div className="resource-orbs">
                                    {Array.from({ length: max }).map((_, i) => (
                                        <div
                                            key={i}
                                            className={`orb ${i < current ? 'filled' : ''} `}
                                            onClick={() => updateResource(key, (i + 1 === current) ? i : i + 1)}
                                            title={`${i + 1} / ${max}`}
                                        />
                                    ))}
                                </div>
                                <div className="resource-text-val">{current} / {max}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Spell Slots Tracker (Always Visible if relevant) */}
            {Object.keys(maxResources).some(k => k.startsWith('spellSlot')) && (
                <div className="combat-section resources-section" style={{ justifyContent: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '20px' }}>
                    {Object.entries(maxResources).map(([key, max]) => {
                        if (!max || max <= 0 || !key.startsWith('spellSlot')) return null;
                        const val = resources[key];
                        const current = val !== undefined ? val : max; // Default full
                        const level = key.replace('spellSlot', '');
                        const label = `${level}${['st', 'nd', 'rd'][level - 1] || 'th'} Level`;

                        return (
                            <div key={key} className="resource-tracker" style={{ '--res-color': '#ff007f', minWidth: '80px' }}>
                                <h4 style={{ fontSize: '0.75rem', opacity: 0.8 }}>{label}</h4>
                                <div className="resource-orbs">
                                    {Array.from({ length: max }).map((_, i) => (
                                        <div
                                            key={i}
                                            className={`orb ${i < current ? 'filled' : ''}`}
                                            onClick={() => updateResource(key, (i + 1 === current) ? i : i + 1)}
                                            title={`${i + 1} / ${max}`}
                                        />
                                    ))}
                                </div>
                                <div className="resource-text-val">{current} / {max}</div>
                            </div>
                        );
                    })}
                    <div className="spell-stats" style={{ marginLeft: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {character.spellSaveDC && (
                            <span className="stat-pill">
                                <span className="stat-pill-label">DC</span>
                                <span className="stat-pill-value">{character.spellSaveDC}</span>
                            </span>
                        )}
                        {character.spellAttackBonus && (
                            <span className="stat-pill">
                                <span className="stat-pill-label">Attack</span>
                                <span className="stat-pill-value">+{character.spellAttackBonus}</span>
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Combat Columns Layout */}
            <div className="combat-columns">
                {/* --- ACTIONS COLUMN --- */}
                <div className="combat-column action-column">
                    <div className="combat-col-header action-header"><h3>Action</h3></div>

                    {/* Attacks */}
                    <div className="attacks-grid">
                        <div className="attack-card unarmed">
                            <div className="attack-header"><span>Unarmed</span></div>
                            <div className="attack-stats">
                                <div className="attack-stat-group"><span className="attack-value">+{unarmed.attackBonus}</span></div>
                                <div className="attack-stat-group"><span className="attack-value damage">{unarmed.damage}{unarmed.damageMod >= 0 ? '+' : ''}{unarmed.damageMod}</span></div>
                            </div>
                            <div className="attack-meta">{unarmed.damageType}</div>
                        </div>
                        {equippedWeapons.map(w => {
                            const { attackBonus, damageMod } = calculateWeaponStats(w, abilityScores, proficiencyBonus);
                            return (
                                <div key={w.id} className="attack-card">
                                    <div className="attack-header"><span>{w.name}</span></div>
                                    <div className="attack-stats">
                                        <div className="attack-stat-group"><span className="attack-value">+{attackBonus}</span></div>
                                        <div className="attack-stat-group"><span className="attack-value damage">{w.damage}{damageMod >= 0 ? '+' : ''}{damageMod}</span></div>
                                    </div>
                                    <div className="attack-meta">{w.damageType}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Action Features/Spells */}
                    <div className="actions-list">
                        {(character.combatActions?.actions || []).map(action => (
                            <div key={action.name} className={`action-row ${action.type === 'Spell' ? 'spell-row' : 'feat-row'}`}>
                                <div className="action-row-content">
                                    <div className="action-title">
                                        <span className="name">{action.name}</span>
                                        <span className="badge">{action.type === 'Spell' ? (action.level === 0 ? 'Cantrip' : `Lvl ${action.level}`) : action.source}</span>
                                    </div>
                                    <div className="action-detail">{action.type === 'Spell' ? action.range : (action.desc || action.description)}</div>
                                </div>
                                <div className="combat-tooltip">
                                    <strong>{action.name}</strong><br />
                                    {action.description || action.desc}
                                </div>
                            </div>
                        ))}
                    </div>



                    {/* Standard Actions */}
                    <div className="standard-actions-list">
                        <h4 className="sub-header">Standard Actions</h4>
                        <div className="actions-chips">
                            {STANDARD_ACTIONS.map(action => (
                                <div key={action.name} className="action-chip">
                                    {action.name}
                                    <div className="combat-tooltip">{action.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- BONUS ACTIONS COLUMN --- */}
                <div className="combat-column bonus-column">
                    <div className="combat-col-header bonus-header"><h3>Bonus Action</h3></div>
                    <div className="actions-list">
                        {(character.combatActions?.bonusActions || []).length > 0 ? (
                            character.combatActions.bonusActions.map(action => (
                                <div key={action.name} className={`action-row ${action.type === 'Spell' ? 'spell-row' : 'feat-row'}`}>
                                    <div className="action-row-content">
                                        <div className="action-title">
                                            <span className="name">{action.name}</span>
                                            <span className="badge">{action.type === 'Spell' ? (action.level === 0 ? 'Cantrip' : `Lvl ${action.level}`) : action.source}</span>
                                        </div>
                                        <div className="action-detail">{action.type === 'Spell' ? action.duration : (action.desc || action.description)}</div>
                                    </div>
                                    <div className="combat-tooltip">
                                        <strong>{action.name}</strong><br />
                                        {action.description || action.desc}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-msg">No Bonus Actions</div>
                        )}
                        {/* Always available Two-Weapon Fighting if 2 light weapons? Logic for another day */}
                    </div>
                </div>

                {/* --- REACTIONS COLUMN --- */}
                <div className="combat-column reaction-column">
                    <div className="combat-col-header reaction-header"><h3>Reaction</h3></div>
                    <div className="actions-list">
                        {(character.combatActions?.reactions || []).length > 0 ? (
                            character.combatActions.reactions.map(action => (
                                <div key={action.name} className={`action-row ${action.type === 'Spell' ? 'spell-row' : 'feat-row'}`}>
                                    <div className="action-row-content">
                                        <div className="action-title">
                                            <span className="name">{action.name}</span>
                                            <span className="badge">{action.type === 'Spell' ? (action.level === 0 ? 'Cantrip' : `Lvl ${action.level}`) : action.source}</span>
                                        </div>
                                        <div className="action-detail">{action.desc || action.description}</div>
                                    </div>
                                    <div className="combat-tooltip">
                                        <strong>{action.name}</strong><br />
                                        {action.description || action.desc}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-msg">No Reactions</div>
                        )}
                        <div className="action-chip" style={{ marginTop: '10px' }}>
                            Opportunity Attack
                            <div className="combat-tooltip">Attack a creature leaving your reach.</div>
                        </div>
                    </div>

                    {/* Free Actions */}
                    {(character.combatActions?.freeActions || []).length > 0 && (
                        <div className="standard-actions-list">
                            <h4 className="sub-header">Free / Special</h4>
                            <div className="actions-list" style={{ marginBottom: 0 }}>
                                {character.combatActions.freeActions.map(action => (
                                    <div key={action.name} className="action-row feat-row">
                                        <div className="action-row-content">
                                            <div className="action-title">
                                                <span className="name">{action.name}</span>
                                                <span className="badge">{action.source}</span>
                                            </div>
                                            <div className="action-detail">{action.desc || action.description}</div>
                                        </div>
                                        <div className="combat-tooltip">
                                            <strong>{action.name}</strong><br />
                                            {action.description || action.desc}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default Combat;
